import './TwoFactorAuthentication.scss';

import { Field, InjectedFormProps } from 'redux-form';
import React, { FunctionComponent, useEffect, useState } from 'react';

import { RadioButton } from '../RadioButton/RadioButton';
import { State } from '../../../types/state';
import { checkMfaIssueTokenApi } from '../../actions/api/checkMfaIssueTokenApi';
import { checkPasswordStatusApi } from '../../actions/api/checkPasswordStatusApi';
import hideSuccessToast from '../../actions/setters/hideSuccessToast';
import { setGlobalAlert } from '../../components/GlobalAlert/GlobalAlert.actions';
import { updateMfaTokenApi } from '../../actions/api/updateMfaTokenApi';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { useSelector } from 'react-redux';
import { useToast } from '@justgiving/toast';
import { useTranslation } from 'react-i18next';

export type SubmitValue = {
  mfa: React.ReactNode;
};

export type Props = {
  checked?: boolean;
  security?: boolean;
  onChange?(e: React.ChangeEvent<HTMLInputElement>): void;
  submitHandler: (value: StatusType) => void;
};

export type SelectedState = {
  isMfaEnabled: boolean;
  isAccessCodeSave: boolean;
  isToastOff: boolean;
};

export enum StatusType {
  enable = 'enable',
  disable = 'disable',
}

const MultiFactorAuthentication: FunctionComponent<InjectedFormProps<
  SubmitValue,
  Props
> &
  Props> = ({ security, submitHandler, handleSubmit }) => {
  const history = useHistory();
  const { t } = useTranslation();
  const { isMfaEnabled, isAccessCodeSave, isToastOff } = useSelector<
    State,
    SelectedState
  >(state => ({
    isMfaEnabled: state.app.mfa.isEnabled,
    isAccessCodeSave: state.app.mfa.isAccessCodeSave,
    isToastOff: state.app.mfa.isToastOff,
  }));

  const reduxDispatch = useDispatch();
  const { showToast } = useToast();

  const checkStatus = () =>
    checkPasswordStatusApi()
      .then(status => status)
      .then(data => setIsEnabled(data.MFAEnabled))
      .catch(() => {
        reduxDispatch(setGlobalAlert(t('apiError.failed_to_post'), 'error'));
      });

  const checkMfaIssueToken = () =>
    checkMfaIssueTokenApi()
      .then(MfaIssue => MfaIssue)
      .then(data => data)
      .catch(() => {
        reduxDispatch(setGlobalAlert(t('apiError.failed_to_post'), 'error'));
      });

  const disableStatus = async () => {
    try {
      const mFAEnabled = await updateMfaTokenApi(isMfaEnabled);

      if (mFAEnabled.Code === 'G200') {
        showToast({
          message: t('accessCode.saveSettings', 'Your changes have been saved'),
          type: 'success',
        });
      }
    } catch (error) {
      reduxDispatch(setGlobalAlert(t('apiError.failed_to_post'), 'error'));
    }
  };

  const [isEnabled, setIsEnabled] = useState<boolean>(checkStatus);

  const onStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (value === StatusType.enable) {
      setIsEnabled(true);
      checkMfaIssueToken();
      submitHandler(value);
    }

    if (value === StatusType.disable) {
      setIsEnabled(false);
      submitHandler(value);
    }
  };

  const onSubmit = (values: SubmitValue) => {
    if (values && values.mfa === StatusType.enable) {
      reduxDispatch(hideSuccessToast(false));
      return history.push('/security/access-code');
    }

    if (values && values.mfa === StatusType.disable) {
      disableStatus();
    }
  };

  useEffect(() => {
    if (isAccessCodeSave && !isToastOff) {
      showToast({
        message: t('accessCode.saveSettings', 'Your changes have been saved'),
        type: 'success',
      });
      reduxDispatch(hideSuccessToast(true));
    }
  }, [isAccessCodeSave, showToast, t, reduxDispatch, isToastOff]);

  return (
    <>
      <form data-testid="form" onSubmit={handleSubmit(onSubmit)}>
        {security ? (
          <div>
            <p className="jg-color--empress jg-text--medium-regular statusWrapper ">
              {' '}
              {t('twoFactorAuthentication.title', 'Two-factor authentication')}
            </p>
            <p className="jg-color--empress jg-text--small-light">
              {isEnabled ? 'Enable' : 'Disable'}
            </p>
          </div>
        ) : (
          <div className="jg-space-psm ">
            <div className="jg-text--medium-light">
              <Field
                component={RadioButton}
                label="Enable"
                value="enable"
                name="mfa"
                type="radio"
                ariaLabel="Enable"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  onStatusChange(e)
                }
                checked={isEnabled === true}
              />

              <Field
                component={RadioButton}
                label="Disable"
                value="disable"
                name="mfa"
                type="radio"
                enable="disable"
                ariaLabel="Disable"
                data-testid="disable"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  onStatusChange(e)
                }
                checked={isEnabled === false}
              />
            </div>
            <button
              type="submit"
              className="jg-btn jg-btn--brand qa-button__submit"
              aria-label={t('common.save', 'Save')}
            >
              {t('common.save', 'Save')}
            </button>
          </div>
        )}
      </form>
    </>
  );
};

export default MultiFactorAuthentication;
